import { useAppContext } from "@/contexts/AppContext";
import {
    Button,
    Form,
    FormTextAreaField,
    FormTextField,
    useForm,
} from "@disasterscience/scaffoldy";
import * as Sentry from "@sentry/browser";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BfFeedbackComponent } from "../lib/api/models";
import { BfFeedback } from "../lib/api/models/bf-feedback";
import { Toast } from "../lib/toast";

type FeedbackProps = {
    feedbackComponent: BfFeedbackComponent;
    redirectAfterSubmitting?: boolean;
    feedbackPrompt?: string;
    feedbackPlaceholder?: string;
    hideEmailPrompt?: boolean;
    customSubmit?: string;
    extraContentToAppend?: string;
};

interface IFeedbackForm {
    message: string;
    email: string;
}

const ROUTE_EXIT: string = "/";

const confirmation: Record<BfFeedbackComponent, string> = {
    [BfFeedbackComponent.cancellation]: "Thank you for your feedback on cancelling!",
    [BfFeedbackComponent.contact]: "Thank you for reaching out!",
    [BfFeedbackComponent.deletion]: "Thank you for your feedback on deletion!",
    [BfFeedbackComponent.error]: "Thank you for your report!",
    [BfFeedbackComponent.general]: "Thank you for reaching out!",
    [BfFeedbackComponent.waitingList]: "Thank you for registering your interest!",
};

const buttonText: Record<BfFeedbackComponent, string> = {
    [BfFeedbackComponent.cancellation]: "Send Cancellation Feedback",
    [BfFeedbackComponent.contact]: "Send",
    [BfFeedbackComponent.deletion]: "Send Deletion Feedback",
    [BfFeedbackComponent.error]: "Send Error Report",
    [BfFeedbackComponent.general]: "Send Feedback",
    [BfFeedbackComponent.waitingList]: "Contact Sales",
};

export const Feedback = (props: FeedbackProps) => {
    const { user, currentDevice, userApi } = useAppContext();
    const navigate = useNavigate();
    const feedbackPrompt =
        props.feedbackPrompt !== undefined ? props.feedbackPrompt : "How can we help?";
    const [feedbackPlaceholder] = useState<string>(
        props.feedbackPlaceholder !== undefined
            ? props.feedbackPlaceholder
            : "My feedback, idea or concern"
    );
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const form = useForm<IFeedbackForm>({
        defaultValues: {
            email: user?.email ? user.email : "",
        },
    });

    const onSubmit = (form: IFeedbackForm) => {
        setIsSubmitting(true);

        // Prepare feedback for submitting
        const feedback = {
            message: form.message,
            email: form.email,
            device: currentDevice(),
            feedbackComponent: props.feedbackComponent,
        } as BfFeedback;

        if (props.extraContentToAppend) {
            feedback.message += " " + props.extraContentToAppend;
        }

        if (
            form.email &&
            (props.feedbackComponent === BfFeedbackComponent.general ||
                props.feedbackComponent === BfFeedbackComponent.error)
        ) {
            Sentry.captureMessage(`Feedback: ${feedback.message} (email: ${feedback.email})`);
        }

        userApi
            .userFeedback(feedback)
            .then(() => {
                setSubmitted(true);
                setIsSubmitting(false);

                Toast.success(confirmation[props.feedbackComponent]);

                // Redirect to home
                if (props.redirectAfterSubmitting) {
                    navigate(ROUTE_EXIT);
                }
            })
            .catch((err) => {
                Toast.errorApi("Something went wrong sending", err);
                setIsSubmitting(false);
            });
    };

    const render = () => {
        if (submitted) {
            return (
                <>
                    <p>Thank you for your feedback! 🚀</p>
                </>
            );
        }

        return (
            <>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit((values) => {
                            onSubmit(values);
                        })}
                    >
                        <div className="tw:flex tw:flex-col tw:gap-4">
                            <FormTextAreaField
                                name="message"
                                label={feedbackPrompt}
                                control={form.control}
                                placeholder={feedbackPlaceholder}
                                required
                            />

                            {!props.hideEmailPrompt && (
                                <FormTextField
                                    name="email"
                                    label="Would you like a response? If so, please provide your email address."
                                    control={form.control}
                                />
                            )}
                            <Button type="submit" disabled={submitted || isSubmitting}>
                                {isSubmitting
                                    ? "Jacking into The Matrix..."
                                    : buttonText[props.feedbackComponent]}
                            </Button>
                        </div>
                    </form>
                </Form>
            </>
        );
    };

    return render();
};
